import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { BiometricIcon, DeleteIcon, LeftArrowIcon } from '../../../assets/icons/icons';
import { useTelegram } from '../../../assets/hooks/useTelegram';
import { useHapticPress } from '../../../assets/hooks/useHapticPress';
import './PinPad.css';

const ROWS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  [null, '0', 'del'],
];
const KEY_TAP_ANIMATION = { scale: 0.92 };
const KEY_TRANSITION = {
  type: 'spring',
  stiffness: 650,
  damping: 34,
  mass: 0.45,
};
const BACK_KEY_INITIAL = { opacity: 0, scale: 0.82, x: -8 };
const BACK_KEY_ANIMATE = { opacity: 1, scale: 1, x: 0 };
const BACK_KEY_TRANSITION = {
  type: 'spring',
  stiffness: 520,
  damping: 30,
  mass: 0.45,
};
const ACTION_KEY_INITIAL = { opacity: 0, scale: 0.82, y: 6 };
const ACTION_KEY_ANIMATE = { opacity: 1, scale: 1, y: 0 };
const ACTION_KEY_EXIT = { opacity: 0, scale: 0.82, y: 6 };

function isEditableTarget(target) {
  if (!target) {
    return false;
  }

  const tagName = target.tagName?.toLowerCase();
  return target.isContentEditable || tagName === 'input' || tagName === 'textarea' || tagName === 'select';
}

function PinPad({
  onDigit,
  onDelete,
  onBack,
  onBiometric,
  showDelete = true,
  disabled = false,
  disabledDigits = [],
}) {
  const { common, pin } = useContext(TranslationContext);
  const { webApp } = useTelegram();
  const shouldReduceMotion = useReducedMotion();
  const tapAnimation = shouldReduceMotion ? undefined : KEY_TAP_ANIMATION;
  const hapticProps = useHapticPress(webApp);
  const [activeHardwareKey, setActiveHardwareKey] = useState(null);
  const disabledDigitSet = useMemo(
    () => new Set(disabledDigits.map(String)),
    [disabledDigits]
  );

  const pressHardwareKey = useCallback((key) => {
    if (shouldReduceMotion) {
      return;
    }

    setActiveHardwareKey(key);
  }, [shouldReduceMotion]);

  const releaseHardwareKey = useCallback((key) => {
    setActiveHardwareKey((currentKey) => (
      currentKey === key ? null : currentKey
    ));
  }, []);

  useEffect(() => {
    if (disabled) {
      setActiveHardwareKey(null);
    }
  }, [disabled]);

  useEffect(() => {
    if (disabled) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (
        event.defaultPrevented
        || event.metaKey
        || event.ctrlKey
        || event.altKey
        || isEditableTarget(event.target)
      ) {
        return;
      }

      if (/^\d$/.test(event.key)) {
        event.preventDefault();
        if (!event.repeat && !disabledDigitSet.has(event.key)) {
          pressHardwareKey(event.key);
          onDigit(event.key);
        }
        return;
      }

      if ((event.key === 'Backspace' || event.key === 'Delete') && onDelete) {
        event.preventDefault();
        if (!event.repeat) {
          pressHardwareKey('delete');
          onDelete();
        }
      }
    };

    const handleKeyUp = (event) => {
      if (/^\d$/.test(event.key)) {
        releaseHardwareKey(event.key);
        return;
      }

      if (event.key === 'Backspace' || event.key === 'Delete') {
        releaseHardwareKey('delete');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [
    disabled,
    disabledDigitSet,
    onDelete,
    onDigit,
    pressHardwareKey,
    releaseHardwareKey,
  ]);

  const handlePress = (callback) => {
    if (disabled) return;

    callback();
  };

  return (
    <div className="pin-pad">
      {ROWS.flatMap((row, rowIndex) =>
        row.map((key, columnIndex) => {
          if (key === null) {
            if (onBack) {
              return (
                <motion.button
                  key="back"
                  type="button"
                  className="pin-pad__key pin-pad__key--action pin-pad__key--back"
                  aria-label={common.back}
                  disabled={disabled}
                  initial={shouldReduceMotion ? false : BACK_KEY_INITIAL}
                  animate={BACK_KEY_ANIMATE}
                  whileTap={tapAnimation}
                  transition={shouldReduceMotion ? undefined : BACK_KEY_TRANSITION}
                  {...hapticProps}
                  onClick={() => handlePress(onBack)}
                >
                  <LeftArrowIcon className="pin-pad__back-icon" />
                </motion.button>
              );
            }

            return (
              <span
                key={`empty-${rowIndex}-${columnIndex}`}
                className="pin-pad__key pin-pad__key--empty"
                aria-hidden="true"
              />
            );
          }

          if (key === 'del') {
            const action = showDelete
              ? {
                key: 'delete',
                label: pin.delete,
                callback: onDelete,
                icon: <DeleteIcon className="pin-pad__delete-icon" />,
              }
              : onBiometric
                ? {
                  key: 'biometric',
                  label: pin.useBiometrics,
                  callback: onBiometric,
                  icon: <BiometricIcon className="pin-pad__biometric-icon" />,
                }
                : null;

            return (
              <span key="action-slot" className="pin-pad__action-slot">
                <AnimatePresence initial={false} mode="wait">
                  {action && (
                    <motion.button
                      key={action.key}
                      type="button"
                      className={[
                        'pin-pad__key pin-pad__key--action',
                        activeHardwareKey === action.key ? 'pin-pad__key--hardware-active' : null,
                      ].filter(Boolean).join(' ')}
                      aria-label={action.label}
                      disabled={disabled}
                      initial={shouldReduceMotion ? false : ACTION_KEY_INITIAL}
                      animate={ACTION_KEY_ANIMATE}
                      exit={shouldReduceMotion ? undefined : ACTION_KEY_EXIT}
                      whileTap={tapAnimation}
                      transition={shouldReduceMotion ? undefined : BACK_KEY_TRANSITION}
                      {...hapticProps}
                      onClick={() => handlePress(action.callback)}
                    >
                      {action.icon}
                    </motion.button>
                  )}
                </AnimatePresence>
              </span>
            );
          }

          const isDigitDisabled = disabled || disabledDigitSet.has(key);

          return (
            <motion.button
              key={key}
              type="button"
              className={[
                'pin-pad__key',
                disabledDigitSet.has(key) ? 'pin-pad__key--digit-disabled' : null,
                activeHardwareKey === key ? 'pin-pad__key--hardware-active' : null,
              ].filter(Boolean).join(' ')}
              disabled={isDigitDisabled}
              whileTap={isDigitDisabled ? undefined : tapAnimation}
              transition={KEY_TRANSITION}
              {...hapticProps}
              onClick={() => handlePress(() => onDigit(key))}
            >
              {key}
            </motion.button>
          );
        })
      )}
    </div>
  );
}

export default PinPad;
